import { useCallback, useEffect, useState } from 'react';
import type { ApiClient, AussparungAbgleichDaten } from '../api';
import {
  ContentSection, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState,
  PropertyGrid, StatusBadge, type Kennwert, type Zustand,
} from '../muster';
import {
  entscheidungname, herkunftname, paarungname, stufenname,
} from '../aussparungKlartext';

/**
 * Der Abgleich conVoid gegen das Architekturmodell.
 *
 * --- Warum es diese Seite gibt ------------------------------------------------
 *
 * Derselbe Durchbruch steht in zwei Modellen: in conVoid mit dem
 * **Freigabestand**, im ARDU-Modell mit der **Anmerkung** des Architekten.
 *
 * Wer nur conVoid liest, sieht, *dass* etwas abgelehnt wurde — aber nicht
 * warum. Die Begründung steht im anderen Modell, und um sie zu lesen, musste
 * bisher jemand Revit öffnen.
 *
 * --- Die fünf Befunde ---------------------------------------------------------
 *
 * ```
 * übereinstimmend      beide Modelle, dieselbe Stelle
 * verschoben           beide Modelle, ANDERE Stelle  ← der teuerste Fall
 * groesse_abweichend   dieselbe Stelle, andere Maße
 * nur_convoid          der Architekt hat sie nicht übernommen
 * nur_architektur      er hat eine ergänzt, die die TGA nicht kennt
 * ```
 *
 * --- Was AS8 geändert hat -----------------------------------------------------
 *
 * Diese Seite hat zwei Monate lang **500 von 6.601** Paaren geladen und keine
 * Gesamtzahl genannt. Der Fachplaner sah ein Zwölftel und konnte es nicht
 * wissen. Jetzt: Seiten statt Schnitt, die Summen über der **ganzen** Menge
 * im Kopf, ein CSV-Weg mit allen — und die Stufen aus F90.
 *
 * --- Und was sie NICHT tut ----------------------------------------------------
 *
 * Sie entscheidet nicht, welches Modell recht hat. Sie zeigt die Abweichung
 * mit Zahl und Stufe; welcher Stand gilt, sagt der Fachplaner, und seine
 * Entscheidung wird **aufgeschrieben** — nicht in ein Modell geschrieben
 * (F61; conVoid ist Paket AS9).
 */

// Der NAME einer Paarungsart steht im Wörterbuch (../aussparungKlartext),
// weil ihn auch der Arbeitsvorrat, der Baum und das Revit-Fenster zeigen.
// Hier bleiben nur der Zustand (die Farbe) und die Erläuterung - die gibt
// es nur auf dieser Seite.
const BEFUNDE: Record<string, { zustand: Zustand; was: string }> = {
  uebereinstimmend: {
    zustand: 'gut',
    was: 'In beiden Modellen an derselben Stelle.',
  },
  verschoben: {
    zustand: 'fehler',
    was: 'In beiden Modellen, aber an unterschiedlicher Stelle. Der teuerste '
       + 'Fall: Im Modell fällt er nicht auf, aber die Leitung passt nicht '
       + 'mehr durch.',
  },
  groesse_abweichend: {
    zustand: 'warnung',
    was: 'Dieselbe Stelle, andere Maße. Meist eine Anpassung des Architekten '
       + '— zu prüfen ist, ob die Leitung noch durchpasst.',
  },
  nur_convoid: {
    zustand: 'warnung',
    was: 'Der Architekt hat sie nicht übernommen. Entweder steht die Übernahme '
       + 'aus, oder sie ist noch offen.',
  },
  nur_architektur: {
    zustand: 'warnung',
    was: 'Der Architekt hat einen Durchbruch ergänzt, den die TGA nicht kennt '
       + '— er gehört in conVoid nachgezogen.',
  },
};

/** Die Farbe einer Versatzstufe. `toleranz` ist keine gute Nachricht mit
 *  Ausrufezeichen, sondern schlicht unauffällig. */
const STUFEN: Record<string, { zustand: Zustand; was: string }> = {
  toleranz: {
    zustand: 'gut',
    was: 'Unter der Versatzgrenze: Zwei Planer treffen denselben Punkt nie '
       + 'auf den Millimeter. Die Paare bleiben zählbar, sie sind nur nichts '
       + 'zum Abarbeiten.',
  },
  befund: {
    zustand: 'warnung',
    was: 'Über der Versatzgrenze. Zu prüfen ist, ob die Leitung an der neuen '
       + 'Stelle noch durchpasst — das sieht man im Modell nicht.',
  },
  anderer_ort: {
    zustand: 'fehler',
    was: 'Über einem Meter. Das ist keine Verschiebung mehr: Entweder ist die '
       + 'Paarung falsch (zwei Löcher, ein Kennzeichen), oder der Durchbruch '
       + 'wurde versetzt und das Gegenstück nie nachgezogen.',
  },
  ohne_abstand: {
    zustand: 'neutral',
    was: 'Kein Abstand gemessen — auf einer Seite fehlt die Lage, oder es '
       + 'gibt gar keine zweite Seite. Eine 0 stünde hier für eine '
       + 'Übereinstimmung, die niemand gemessen hat.',
  },
};

/** Die Entscheidungen, die der Fachplaner treffen kann (AS8, 0823). */
const ENTSCHEIDUNGEN: { schluessel: string; knopf: string; was: string }[] = [
  {
    schluessel: 'bestaetigt',
    knopf: 'stimmt überein',
    was: 'Beide Stände sind richtig, nichts zu tun.',
  },
  {
    schluessel: 'architektur_fuehrt',
    knopf: 'Architektur führt',
    was: 'Der Stand des Architekturmodells gilt; conVoid zieht nach. Das ist '
       + 'eine Anweisung an den Planer — die Plattform schreibt in kein '
       + 'Modell (F61).',
  },
  {
    schluessel: 'convoid_fuehrt',
    knopf: 'conVoid führt',
    was: 'Der conVoid-Stand gilt; die Architektur zieht nach.',
  },
  {
    schluessel: 'zurueckgestellt',
    knopf: 'zurückgestellt',
    was: 'Gesehen, aber nicht entscheidbar — mit Begründung.',
  },
];

const z0 = (x: number | null | undefined) =>
  x == null ? '—' : x.toLocaleString('de-DE', { maximumFractionDigits: 0 });

export function AussparungAbgleich({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [daten, setDaten] = useState<AussparungAbgleichDaten | null>(null);
  const [befund, setBefund] = useState<string>('');
  const [stufe, setStufe] = useState<string>('');
  const [geschoss, setGeschoss] = useState<string>('');
  const [entscheidung, setEntscheidung] = useState<string>('');
  const [seite, setSeite] = useState(1);
  const [groesse, setGroesse] = useState(100);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(() => {
    api.aussparungAbgleich(projektId, {
      befund: befund || undefined,
      stufe: stufe || undefined,
      geschoss: geschoss || undefined,
      entscheidung: entscheidung || undefined,
      seite,
      groesse,
    })
      .then((x) => { setDaten(x); setFehler(null); })
      .catch(setFehler);
  }, [api, projektId, befund, stufe, geschoss, entscheidung, seite, groesse]);

  useEffect(() => { laden(); }, [laden]);

  // Ein neuer Filter fängt wieder auf Seite 1 an. Bliebe die Seite stehen,
  // zeigte die Liste nach dem Filtern nichts an, obwohl es Treffer gibt.
  const filtern = (setzen: () => void) => { setzen(); setSeite(1); };

  const paaren = async () => {
    setLaeuft(true);
    try {
      await api.aussparungPaaren(projektId);
      laden();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  if (fehler && !daten) {
    return (
      <ErrorState
        meldung="Der Abgleich mit dem Architekturmodell konnte nicht geladen werden."
        technisch={fehler}
        beiErneutVersuchen={laden}
      />
    );
  }

  if (!daten) {
    return <LoadingState text="Abgleich wird geladen …" zeilen={6} spalten={2} />;
  }

  const stufenzahl = (s: string) =>
    daten.jeStufe.find((x) => x.stufe === s)?.stueck ?? 0;

  // Der Bericht folgt dem Geschossfilter: Wer G02 abarbeitet, bekommt das
  // Blatt seines Geschosses.
  const bericht = api.aussparungAbgleichPdfPfad(
    projektId, geschoss || undefined);

  const csv = api.aussparungAbgleichCsvPfad(projektId, {
    befund: befund || undefined,
    stufe: stufe || undefined,
    geschoss: geschoss || undefined,
    entscheidung: entscheidung || undefined,
  });

  return (
    <>
      <ContentSection
        titel="Abgleich mit dem Architekturmodell"
        beschreibung={'Derselbe Durchbruch steht in zwei Modellen: in conVoid mit dem '
          + 'Freigabestand, im ARDU-Modell mit der Anmerkung des Architekten. Wer nur '
          + 'conVoid liest, sieht, DASS etwas abgelehnt wurde — aber nicht warum. '
          + 'Dieser Abgleich entscheidet nicht, welches Modell recht hat: Er zeigt '
          + 'die Abweichung mit Zahl, und die Entscheidung des Fachplaners wird '
          + 'festgehalten — in keinem Modell (F61).'}
        aktionen={(
          <>
            <a className="m-knopf" href={bericht} download>
              {geschoss ? `Bericht ${geschoss}` : 'Bericht (alle Geschosse)'}
            </a>
            <a className="m-knopf" href={csv} download>
              alle als CSV
            </a>
            <button className="m-knopf m-knopf-haupt" disabled={laeuft} onClick={paaren}>
              {laeuft ? 'paart …' : 'neu paaren'}
            </button>
          </>
        )}
      >
        <KpiReihe>
          <KpiCard
            titel="Paare im Abgleich"
            wert={daten.paare}
            vergleich={`${z0(daten.stillgelegt)} weitere hängen an `
              + 'stillgelegten Aussparungen und stehen im Reiter „entfernt" '
              + '(F87)'}
          />
          <KpiCard
            titel="über der Versatzgrenze"
            wert={stufenzahl('befund') + stufenzahl('anderer_ort')}
            zustand={stufenzahl('befund') + stufenzahl('anderer_ort') > 0
              ? 'warnung' : 'gut'}
            zustandText={stufenzahl('befund') + stufenzahl('anderer_ort') > 0
              ? 'zu prüfen' : 'keiner'}
            vergleich={`davon ${z0(stufenzahl('anderer_ort'))} über einem Meter`}
            fussnote={daten.grenzen.klartext}
          />
          <KpiCard
            titel="Paarung bestätigt"
            wert={daten.bestaetigt}
            vergleich={`von ${z0(daten.paare)} — „bestätigt" heißt: dieselben `
              + 'zwei Löcher, nicht: dieser Stand gilt'}
          />
          <KpiCard
            titel="entschieden"
            wert={daten.entschieden}
            vergleich={`von ${z0(daten.paare)} — welcher Stand gilt, sagt der `
              + 'Fachplaner'}
          />
        </KpiReihe>

        {!daten.grenzen.ausDerDatenbank && (
          <ErrorState klein meldung={daten.grenzen.klartext} />
        )}

        {daten.hinweise.map((h) => (
          <p key={h} style={{ fontSize: 12, marginTop: 8 }}>{h}</p>
        ))}

        {/* --- Filter: Befund --- */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                      alignItems: 'center', marginTop: 12 }}>
          <span className="schwach" style={{ fontSize: 11, width: 66 }}>
            Befund
          </span>

          <button className={befund === '' ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                  aria-pressed={befund === ''}
                  onClick={() => filtern(() => setBefund(''))}>
            alle ({z0(daten.paare)})
          </button>

          {daten.jeBefund.map((b) => (
            <button key={b.befund}
                    className={befund === b.befund ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                    aria-pressed={befund === b.befund}
                    onClick={() => filtern(() => setBefund(b.befund))}>
              <StatusBadge
                klein
                zustand={BEFUNDE[b.befund]?.zustand ?? 'neutral'}
                text={`${paarungname(b.befund)} (${z0(b.stueck)})`}
              />
            </button>
          ))}
        </div>

        {/* --- Filter: Versatzstufe (F90) --- */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                      alignItems: 'center', marginTop: 8 }}>
          <span className="schwach" style={{ fontSize: 11, width: 66 }}>
            Versatz
          </span>

          <button className={stufe === '' ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                  aria-pressed={stufe === ''}
                  onClick={() => filtern(() => setStufe(''))}>
            alle
          </button>

          {daten.jeStufe.map((s) => (
            <button key={s.stufe}
                    className={stufe === s.stufe ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                    aria-pressed={stufe === s.stufe}
                    onClick={() => filtern(() => setStufe(s.stufe))}>
              <StatusBadge
                klein
                zustand={STUFEN[s.stufe]?.zustand ?? 'neutral'}
                text={`${stufenname(s.stufe)} (${z0(s.stueck)})`}
              />
            </button>
          ))}
        </div>

        {/* --- Filter: Geschoss und Entscheidung --- */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                      alignItems: 'center', marginTop: 8 }}>
          <span className="schwach" style={{ fontSize: 11, width: 66 }}>
            Geschoss
          </span>

          <select className="m-feld" value={geschoss}
                  onChange={(e) => filtern(() => setGeschoss(e.target.value))}>
            <option value="">alle Geschosse</option>
            {daten.jeGeschoss.map((g) => (
              <option key={g.geschoss} value={g.geschoss === '—' ? '' : g.geschoss}>
                {g.geschoss} ({g.stueck})
              </option>
            ))}
          </select>

          <select className="m-feld" value={entscheidung}
                  onChange={(e) => filtern(() => setEntscheidung(e.target.value))}>
            <option value="">jede Entscheidung</option>
            {daten.jeEntscheidung.map((e2) => (
              <option key={e2.entscheidung} value={e2.entscheidung}>
                {entscheidungname(e2.entscheidung)} ({e2.stueck})
              </option>
            ))}
          </select>

          <select className="m-feld" value={groesse}
                  onChange={(e) => filtern(() => setGroesse(Number(e.target.value)))}>
            {[50, 100, 250, 500, 1000].map((n) => (
              <option key={n} value={n}>{n} je Seite</option>
            ))}
          </select>
        </div>

        {befund && BEFUNDE[befund] && (
          <p style={{ fontSize: 13, marginTop: 12 }}>{BEFUNDE[befund].was}</p>
        )}

        {stufe && STUFEN[stufe] && (
          <p style={{ fontSize: 13, marginTop: 6 }}>{STUFEN[stufe].was}</p>
        )}

        {fehler !== null && (
          <ErrorState klein meldung="Die letzte Aktion ist fehlgeschlagen."
                      technisch={fehler} />
        )}
      </ContentSection>

      <ContentSection
        titel="Paare"
        beschreibung={'Die Herkunft „lage" heißt: über den Abstand gepaart, nicht über '
          + 'das Kennzeichen. Das ist eine VERMUTUNG — zwei Durchbrüche nebeneinander '
          + 'sind so nicht zu unterscheiden. Eine Bestätigung von Hand macht daraus '
          + 'eine Zuordnung; welcher Stand GILT, ist die nächste Frage und ein '
          + 'eigenes Feld.'}
        meta={daten.gesamt === 0 ? '0' : `${daten.zeilen.length} von `
          + `${z0(daten.gesamt)} · Seite ${daten.seite} von ${daten.seiten}`}
      >
        <Seitenschalter daten={daten} setSeite={setSeite} />

        {daten.zeilen.map((p2) => (
          <Paar key={p2.id} paar={p2} api={api} neuLaden={laden}
                gewerkrang={daten.gewerkrang} />
        ))}

        {daten.zeilen.length === 0 && (
          <EmptyState
            titel="Kein Paar mit diesem Filter."
            erklaerung={befund || stufe || geschoss || entscheidung
              ? 'Die Summen oben stehen über der ganzen Menge — dort steht, '
                + 'wo es welche gibt.'
              : 'Es liegt noch keine Paarung vor — „neu paaren" stellt sie her.'}
            aktion={(befund || stufe || geschoss || entscheidung) ? (
              <button className="m-knopf" onClick={() => {
                setBefund(''); setStufe(''); setGeschoss('');
                setEntscheidung(''); setSeite(1);
              }}>
                Filter zurücksetzen
              </button>
            ) : undefined}
          />
        )}

        {daten.zeilen.length > 0 && (
          <Seitenschalter daten={daten} setSeite={setSeite} />
        )}
      </ContentSection>
    </>
  );
}

/**
 * Vor und zurück — und die Zahl dazu.
 *
 * Ein Seitenschalter ohne Gesamtzahl ist derselbe Fehler wie ein `Take(500)`
 * ohne Hinweis: Man sieht nicht, wie viel man nicht sieht.
 */
function Seitenschalter({ daten, setSeite }: {
  daten: AussparungAbgleichDaten; setSeite: (n: number) => void;
}) {
  if (daten.seiten <= 1) return null;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                  padding: '8px 0' }}>
      <button className="m-knopf" disabled={daten.seite <= 1}
              onClick={() => setSeite(daten.seite - 1)}>
        ← zurück
      </button>

      <span style={{ fontSize: 12 }}>
        Seite <strong>{daten.seite}</strong> von {daten.seiten}
        {' · '}
        {daten.gesamt.toLocaleString('de-DE')} Paare im Filter
      </span>

      <button className="m-knopf" disabled={daten.seite >= daten.seiten}
              onClick={() => setSeite(daten.seite + 1)}>
        weiter →
      </button>
    </div>
  );
}

function Paar({ paar, api, neuLaden, gewerkrang }: {
  paar: AussparungAbgleichDaten['zeilen'][number];
  api: ApiClient;
  neuLaden: () => void;
  gewerkrang: AussparungAbgleichDaten['gewerkrang'];
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState('');

  const b = BEFUNDE[paar.befund];
  const s = paar.stufe ? STUFEN[paar.stufe] : undefined;

  const bestaetigen = async () => {
    setLaeuft(true);
    try {
      await api.aussparungPaarBestaetigen(paar.id);
      neuLaden();
    } finally {
      setLaeuft(false);
    }
  };

  const entscheiden = async (welche: string) => {
    setLaeuft(true);
    try {
      await api.aussparungPaarEntscheiden(paar.id, welche, grund || undefined);
      setOffen(false);
      setGrund('');
      neuLaden();
    } finally {
      setLaeuft(false);
    }
  };

  const mass = (x: {
    form: string | null; breiteMm: number | null; hoeheMm: number | null;
    durchmesserMm: number | null;
  }) => (x.durchmesserMm
    ? `⌀ ${x.durchmesserMm}`
    : `${x.breiteMm ?? '?'} × ${x.hoeheMm ?? '?'}`);

  /**
   * Die Gewerke der conVoid-Seite — mit der Rangfolge und der OFFENEN FRAGE.
   *
   * `GewerkFuehrend` bleibt bei kombinierten Gewerken leer, und das ist
   * Absicht: Ob aus „RLTAHZGA" ein Sammelkürzel oder ein führendes Gewerk
   * wird, ist eine Festlegung des Bauherrn. Die Reihung steht seit 0294 in
   * der Tabelle — sie zu zeigen ist etwas anderes, als sie anzuwenden.
   */
  const gewerkzeile = (): string => {
    const roh = paar.conVoid?.gewerke ?? paar.conVoid?.gewerkRoh ?? null;

    if (!roh) return '—';
    if (paar.conVoid?.gewerkFuehrend) {
      return `${roh} — führend: ${paar.conVoid.gewerkFuehrend}`;
    }
    if (!roh.includes(',')) return roh;

    const geordnet = roh.split(',').map((x) => x.trim())
      .sort((x, y) => (gewerkrang.find((r) => r.kuerzel === x)?.rang ?? 99)
                    - (gewerkrang.find((r) => r.kuerzel === y)?.rang ?? 99));

    return `${geordnet.join(', ')} — führendes Gewerk offen (Festlegung des `
         + 'Bauherrn, nicht abgeleitet)';
  };

  /** Die conVoid-Seite als Kennwerttafel (Abschnitt 17). */
  const conVoidWerte: Kennwert[] = paar.conVoid ? [
    { beschriftung: 'Kennzeichen', wert: paar.conVoid.kennzeichen },
    {
      beschriftung: 'Geschoss · Maß',
      wert: `${paar.conVoid.geschoss ?? '—'} · ${mass(paar.conVoid)}`,
    },
    { beschriftung: 'Gewerk', wert: gewerkzeile(), breit: true },
    { beschriftung: 'Zustand', wert: paar.conVoid.zustand },
    {
      beschriftung: 'Freigabe',
      wert: paar.conVoid.freigegebenVon
        ? <StatusBadge zustand="gut" klein
                       text={`freigegeben von ${paar.conVoid.freigegebenVon}`} />
        : paar.conVoid.abgelehntVon
          ? <StatusBadge zustand="fehler" klein
                         text={`abgelehnt von ${paar.conVoid.abgelehntVon}`} />
          : paar.conVoid.offenBei
            ? <StatusBadge zustand="warnung" klein
                           text={`offen bei ${paar.conVoid.offenBei}`} />
            : null,
      breit: true,
    },
    {
      beschriftung: 'Anforderung',
      wert: paar.conVoid.anforderungRoh === 'TBC?'
        ? <StatusBadge zustand="warnung" text="TBC?" klein
                       titel="to be confirmed — nicht bestätigt" />
        : paar.conVoid.anforderungRoh,
    },
  ] : [];

  const architekturWerte: Kennwert[] = paar.architektur ? [
    { beschriftung: 'Kennzeichen', wert: paar.architektur.kennzeichen },
    {
      beschriftung: 'Geschoss · Maß',
      wert: `${paar.architektur.geschoss ?? '—'} · ${mass(paar.architektur)}`,
    },
    { beschriftung: 'Modell', wert: paar.architektur.verknuepfung, breit: true },
  ] : [];

  return (
    <div style={{
      borderTop: '1px solid var(--linie)', paddingTop: 10, marginTop: 10,
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline',
                    flexWrap: 'wrap' }}>
        <StatusBadge zustand={b?.zustand ?? 'neutral'}
                     text={paarungname(paar.befund)} />

        {paar.abstandMm != null && paar.abstandMm > 0 && (
          <span style={{ fontSize: 13 }}>
            {z0(paar.abstandMm)} mm versetzt
            {paar.richtung && paar.richtung !== '—' && (
              <span className="schwach"> · {paar.richtung}</span>
            )}
          </span>
        )}

        {paar.stufe && s && (
          <StatusBadge klein zustand={s.zustand}
                       text={stufenname(paar.stufe)} titel={s.was} />
        )}

        {paar.groessenabweichungMm != null
          && paar.groessenabweichungMm > 0 && (
          <span style={{ fontSize: 13 }}>
            {z0(paar.groessenabweichungMm)} mm Maßabweichung
          </span>
        )}

        <span className="schwach" style={{ fontSize: 11 }}>
          gepaart {herkunftname(paar.herkunft)}
        </span>

        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {paar.bestaetigt
            ? <StatusBadge zustand="gut" text="Paarung bestätigt" klein />
            : paar.herkunft === 'lage' && (
              <button className="m-knopf" disabled={laeuft} onClick={bestaetigen}>
                Paarung bestätigen
              </button>
            )}

          {paar.entscheidung
            ? (
              <StatusBadge
                klein
                zustand={paar.entscheidung === 'zurueckgestellt'
                  ? 'warnung' : 'gut'}
                text={entscheidungname(paar.entscheidung)}
                titel={`${paar.entschiedenVon ?? '—'}`
                     + `${paar.entschiedenAm
                          ? ` · ${paar.entschiedenAm.slice(0, 10)}` : ''}`}
              />
            )
            : (
              <button className="m-knopf" disabled={laeuft}
                      onClick={() => setOffen((x) => !x)}>
                {offen ? 'abbrechen' : 'entscheiden …'}
              </button>
            )}
        </span>
      </div>

      {/* --- Die Entscheidung: welcher Stand gilt --- */}
      {offen && (
        <div style={{
          background: 'var(--flaeche)', border: '1px solid var(--linie)',
          borderRadius: 4, padding: '8px 10px', marginTop: 8, fontSize: 12,
        }}>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Welcher Stand gilt?</strong> Die Entscheidung ändert kein
            Modell — sie wird festgehalten (F61). Wer sie ausführt, tut es in
            Revit.
          </p>

          <input
            className="m-feld"
            style={{ width: '100%', marginBottom: 6 }}
            placeholder="Begründung (ohne sie ist die Entscheidung in vier Wochen nicht mehr zu deuten)"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
          />

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ENTSCHEIDUNGEN.map((e) => (
              <button key={e.schluessel} className="m-knopf" disabled={laeuft}
                      title={e.was}
                      onClick={() => entscheiden(e.schluessel)}>
                {e.knopf}
              </button>
            ))}
          </div>
        </div>
      )}

      {paar.entscheidung && paar.begruendung && (
        <p style={{ fontSize: 12, marginTop: 6 }}>
          <strong>Begründung:</strong> {paar.begruendung}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: 12, marginTop: 8 }}>
        {/* --- conVoid --- */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-schwach)' }}>
            conVoid — die TGA-Seite
          </div>

          {paar.conVoid ? (
            <PropertyGrid spalten={1} dicht eintraege={conVoidWerte} />
          ) : (
            <p style={{ marginTop: 4 }}>
              <StatusBadge
                zustand="warnung" klein
                text="Nicht in conVoid vorhanden"
                titel="Der Architekt hat sie ergänzt."
              />
            </p>
          )}
        </div>

        {/* --- Architektur --- */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-schwach)' }}>
            Architekturmodell — die Anmerkung
          </div>

          {paar.architektur ? (
            <>
              <PropertyGrid spalten={1} dicht eintraege={architekturWerte} />

              {paar.architektur.anmerkung ? (
                <div style={{
                  background: 'var(--flaeche)', border: '1px solid var(--linie)',
                  borderRadius: 4, padding: '6px 8px', marginTop: 6,
                  fontSize: 13,
                }}>
                  {paar.architektur.anmerkung}

                  {paar.architektur.anmerkungFeld && (
                    <div className="schwach" style={{ fontSize: 10, marginTop: 4 }}>
                      aus dem Feld „{paar.architektur.anmerkungFeld}"
                    </div>
                  )}
                </div>
              ) : (
                <p className="schwach" style={{ fontSize: 12, marginTop: 6 }}>
                  Keine Anmerkung.
                </p>
              )}

              {paar.architektur.pruefvermerk && (
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  <strong>Prüfvermerk:</strong>{' '}
                  {paar.architektur.pruefvermerk}
                  {paar.architektur.pruefer && ` (${paar.architektur.pruefer})`}
                </div>
              )}
            </>
          ) : (
            <p style={{ marginTop: 4 }}>
              <StatusBadge
                zustand="warnung" klein
                text="Nicht im Architekturmodell"
                titel="Der Architekt hat sie nicht übernommen."
              />
            </p>
          )}
        </div>
      </div>

      {paar.bemerkung && (
        <p className="schwach" style={{ fontSize: 11, marginTop: 6 }}>
          {paar.bemerkung}
        </p>
      )}
    </div>
  );
}
